import { Flex, Image, Stack, Text } from '@mantine/core'
import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone'
import { IconButton } from '@mui/material'
import { IconX } from '@tabler/icons-react'
import axios from 'axios'
import { useEffect, useState } from 'react'
import { createPublishedChapter } from 'src/apis/bookAPI'
import { uploadDocFile, uploadProgress } from 'src/apis/uploadApi'
import DocFileCard from 'src/components/BookUniVini/DocFileCard'
import CustomButton from 'src/components/CustomComponents/CustomButton'
import CustomDrawer from 'src/components/CustomComponents/CustomDrawer'
import CustomInput from 'src/components/CustomComponents/CustomInput'
import CustomProgress from 'src/components/CustomComponents/CustomProgress'
import { useModal } from 'src/context/ModalContext'
import { BookData } from 'src/interfaces'
import {
	DOCX_DROPZONE_ACCEPT,
	pickDocWordFiles,
} from 'src/utils/docxDropzone'
import { getBookCoverImageUrl, getBookTitle } from 'src/utils/bookCoverImage'

interface ChapterCreateDrawerProps {
	opened: boolean
	onClose: () => void
	book: BookData | null
	onRefresh?: () => void | Promise<void>
}

type ChapterCreateForm = {
	title: string
	summary: string
	thumbnail: string
	docUrl: string
}

const TITLE_MAX = 45
const SUMMARY_MAX = 500

const EMPTY_FORM: ChapterCreateForm = {
	title: '',
	summary: '',
	thumbnail: '',
	docUrl: '',
}

function ChapterCreateDrawer({
	opened,
	onClose,
	book,
	onRefresh,
}: ChapterCreateDrawerProps) {
	const { openError, openModal } = useModal()
	const [saving, setSaving] = useState(false)
	const [form, setForm] = useState<ChapterCreateForm>(EMPTY_FORM)
	const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null)
	const [thumbnailProgress, setThumbnailProgress] = useState(0)
	const [docProgress, setDocProgress] = useState(0)

	useEffect(() => {
		if (!opened) return
		setForm(EMPTY_FORM)
		setThumbnailPreview(null)
		setThumbnailProgress(0)
		setDocProgress(0)
	}, [opened, book?.id])

	const bookCoverUrl = getBookCoverImageUrl(book)
	const bookTitle = getBookTitle(book)
	const normalizedBookTitle = bookTitle.slice(0, TITLE_MAX)
	const hasThumbnail = Boolean(form.thumbnail || thumbnailPreview)
	const isUsingBookCover = Boolean(
		bookCoverUrl && form.thumbnail === bookCoverUrl && !thumbnailPreview,
	)
	const isUsingBookTitle = Boolean(bookTitle && form.title === normalizedBookTitle)

	const canSubmit = Boolean(book?.id) && Boolean(form.docUrl)

	const handleRemoveThumbnail = () => {
		setForm((prev) => ({ ...prev, thumbnail: '' }))
		setThumbnailPreview(null)
	}

	const handleUseBookCover = () => {
		if (!bookCoverUrl) {
			openError(new Error('This book has no cover image'))
			return
		}

		setForm((prev) => ({ ...prev, thumbnail: bookCoverUrl }))
		setThumbnailPreview(null)
	}

	const handleUseBookTitle = () => {
		if (!bookTitle) {
			openError(new Error('This book has no title'))
			return
		}

		setForm((prev) => ({ ...prev, title: normalizedBookTitle }))
	}

	const handleUploadThumbnail = async (files: File[]) => {
		const file = files[0]
		if (!file) return

		setThumbnailPreview(URL.createObjectURL(file))
		setThumbnailProgress(0)

		try {
			const imageUrl = await uploadProgress(file, (event) => {
				const { loaded, total } = event
				if (total) {
					setThumbnailProgress(Math.round((loaded * 100) / total))
				}
			})
			setForm((prev) => ({ ...prev, thumbnail: imageUrl }))
		} catch (error) {
			openError(error)
		}
	}

	const handleUploadDoc = async (files: File[]) => {
		const file = pickDocWordFiles(files)[0]
		if (!file) {
			openError(new Error('Only .doc or .docx files are allowed'))
			return
		}

		setDocProgress(0)
		try {
			const docUrl = await uploadDocFile(file, (event) => {
				const { loaded, total } = event
				if (total) {
					setDocProgress(Math.round((loaded * 100) / total))
				}
			})

			if (!docUrl) {
				openError(new Error('Upload failed — no URL returned'))
				return
			}

			setForm((prev) => ({ ...prev, docUrl }))
		} catch (error) {
			if (!axios.isCancel(error)) {
				openError(error)
			}
		}
	}

	const handleRejectDoc = (files: File[]) => {
		const docFiles = pickDocWordFiles(files)
		if (docFiles.length) {
			void handleUploadDoc(docFiles)
			return
		}

		openError(new Error('Only .doc or .docx files are allowed'))
	}

	const handleCreate = async () => {
		if (!book?.id) return

		const title = form.title.trim()
		if (!form.docUrl) {
			openError(new Error('English DOCX file is required'))
			return
		}

		setSaving(true)
		try {
			const res = await createPublishedChapter({
				book_id: book.id,
				english_doc_url: form.docUrl,
				title: title || undefined,
				summary: form.summary.trim() || undefined,
				thumbnail: form.thumbnail || null,
			})

			if (res?.data?.code === 200 || res?.status === 200 || res?.data?.code === 201) {
				openModal({ type: 'success', label: 'Chapter created successfully' })
				await onRefresh?.()
				onClose()
			}
		} catch (error) {
			openError(error)
		} finally {
			setSaving(false)
		}
	}

	return (
		<CustomDrawer
			opened={opened}
			onClose={onClose}
			width={640}
			loading={saving}
			title={
				<Stack spacing={4}>
					<Text size="xs" color="dimmed">
						UniVini · Add a chapter
					</Text>
					<Text weight={700}>{book?.title || '-'}</Text>
					<Text size="sm" color="dimmed">
						Upload English DOCX to create a new chapter
					</Text>
				</Stack>
			}
			footer={
				<CustomButton
					fullWidth
					onClick={handleCreate}
					disabled={!canSubmit || saving}
					sx={{
						height: 44,
						border: 'none',
						color: '#fff',
						background: canSubmit ? '#1f7a44' : '#cbd5e1',
						':hover': {
							background: canSubmit ? '#195c34 !important' : '#cbd5e1',
							color: '#fff',
						},
					}}
				>
					Create chapter
				</CustomButton>
			}
		>
			<Stack spacing={20} sx={{ padding: 16 }}>
				<Text size="sm" color="dimmed">
					If your book has no chapters, upload an English DOCX file to get started.
				</Text>

				<Stack spacing={8}>
					<Text
						size="xs"
						weight={700}
						sx={{ textTransform: 'uppercase', color: '#6b7280', letterSpacing: 0.4 }}
					>
						Chapter thumbnail (optional)
					</Text>
					{thumbnailProgress > 0 && thumbnailProgress < 100 && (
						<CustomProgress value={thumbnailProgress} />
					)}
					{hasThumbnail && (
						<BoxWithRemove onRemove={handleRemoveThumbnail}>
							<Image
								src={thumbnailPreview || form.thumbnail}
								alt="Chapter thumbnail"
								height={180}
								width="auto"
								fit="contain"
								sx={{ maxWidth: '100%' }}
							/>
						</BoxWithRemove>
					)}
					<Dropzone
						accept={IMAGE_MIME_TYPE}
						onDrop={handleUploadThumbnail}
						multiple={false}
						activateOnClick={!hasThumbnail}
						sx={{
							border: '1px dashed #dbe6dc',
							borderRadius: 12,
							background: '#fafcfa',
							position: 'relative',
						}}
					>
						<Text size="sm" color="dimmed" align="center">
							{hasThumbnail
								? 'Drag and drop to replace thumbnail'
								: 'Drag and drop thumbnail or click to browse'}
						</Text>
					</Dropzone>
					<CustomButton
						variant="outlined"
						onClick={handleUseBookCover}
						disabled={!bookCoverUrl || isUsingBookCover}
						sx={{ height: 36, alignSelf: 'flex-start' }}
					>
						{isUsingBookCover ? 'Using book cover image' : 'Use book cover image'}
					</CustomButton>
				</Stack>

				<Stack spacing={6}>
					<CustomInput
						label="Chapter title (optional)"
						value={form.title}
						maxLength={TITLE_MAX}
						onChange={(e) =>
							setForm((prev) => ({ ...prev, title: e.target.value }))
						}
						placeholder="Chapter title"
					/>
					<Flex justify="space-between" align="center">
						<CustomButton
							variant="outlined"
							onClick={handleUseBookTitle}
							disabled={!bookTitle || isUsingBookTitle}
							sx={{ height: 36 }}
						>
							{isUsingBookTitle ? 'Using book title' : 'Use book title'}
						</CustomButton>
						<Text size="xs" color="dimmed">
							{form.title.length}/{TITLE_MAX}
						</Text>
					</Flex>
				</Stack>

				<Stack spacing={6}>
					<CustomInput
						label="Chapter summary"
						value={form.summary}
						maxLength={SUMMARY_MAX}
						onChange={(e) =>
							setForm((prev) => ({ ...prev, summary: e.target.value }))
						}
						placeholder="Describe"
						multiline
						rows={4}
					/>
					<Flex justify="flex-end">
						<Text size="xs" color="dimmed">
							{form.summary.length}/{SUMMARY_MAX}
						</Text>
					</Flex>
				</Stack>

				<Stack spacing={12}>
					<Text size="sm" weight={700} sx={{ textTransform: 'uppercase' }}>
						Upload files
					</Text>

					<Text size="sm" weight={600}>
						English
					</Text>

					{docProgress > 0 && docProgress < 100 && (
						<CustomProgress value={docProgress} />
					)}

					{form.docUrl ? (
						<DocFileCard
							url={form.docUrl}
							onDelete={() => setForm((prev) => ({ ...prev, docUrl: '' }))}
							showDelete
						/>
					) : (
						<Text size="sm" color="dimmed">
							No English DOCX uploaded
						</Text>
					)}

					<Dropzone
						accept={DOCX_DROPZONE_ACCEPT}
						onDrop={handleUploadDoc}
						onReject={(rejections) =>
							handleRejectDoc(rejections.map((item) => item.file))
						}
						multiple={false}
						sx={{
							border: '1px dashed #dbe6dc',
							borderRadius: 12,
							background: '#fafcfa',
							padding: 16,
						}}
					>
						<Text size="sm" color="dimmed" align="center">
							Drag and drop DOCX file or click to browse
						</Text>
					</Dropzone>
				</Stack>
			</Stack>
		</CustomDrawer>
	)
}

function BoxWithRemove({
	children,
	onRemove,
}: {
	children: React.ReactNode
	onRemove: () => void
}) {
	return (
		<div style={{ position: 'relative', width: '100%' }}>
			<IconButton
				onMouseDown={(event) => {
					event.preventDefault()
					event.stopPropagation()
				}}
				onClick={(event) => {
					event.preventDefault()
					event.stopPropagation()
					onRemove()
				}}
				size="small"
				aria-label="Remove thumbnail"
				sx={{
					position: 'absolute',
					top: 8,
					right: 8,
					zIndex: 2,
					background: '#fff',
					boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
				}}
			>
				<IconX size={16} />
			</IconButton>
			{children}
		</div>
	)
}

export default ChapterCreateDrawer
